<script setup lang="ts">
import { computed, useSlots } from 'vue';

import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';

import type {
  AppFormButtonIconProperties,
  AppFormButtonSize,
  AppFormButtonState,
  AppFormButtonTextAlign,
} from './AppFormButton.model';

export interface AppFormButtonProps {
  size?: AppFormButtonSize;
  state?: AppFormButtonState;
  icon?: AppFormButtonIconProperties;
  wide?: boolean;
  textAlign?: AppFormButtonTextAlign;
  disabled?: boolean;
}

const props = withDefaults(defineProps<AppFormButtonProps>(), {
  state: 'enabled',
  size: 'l',
});

interface AppFormButtonEmits {
  (click: 'click', event: MouseEvent): void;
}

const emit = defineEmits<AppFormButtonEmits>();

const slots = useSlots();
const hasTextSlot = computed(() => !!slots['default']);

const textMargin = computed(() => {
  if (!props.icon || !hasTextSlot.value) {
    return undefined;
  }
  return props.icon.position === 'left' ? 'left-padding' : 'right-padding';
});
const iconSize = computed(() => {
  if (props.icon && props.icon.size) {
    return props.icon.size;
  } else {
    return '24';
  }
});

function handleClick(event: MouseEvent): void {
  if (props.state === 'enabled') {
    emit('click', event);
  }
}
</script>

<template>
  <button
    class="app-form-button"
    :class="[
      [props.size],
      [props.state],
      [textMargin],
      props.wide ? 'wide' : undefined,
      props.disabled ? 'disabled' : undefined,
      props.icon?.position === 'left' ? 'left-icon' : 'right-icon',
      hasTextSlot ? undefined : 'square',
      [props.textAlign],
    ]"
    @click="handleClick"
  >
    <AppSvgIcon v-if="props.icon" :name="props.icon.name" :size="iconSize" />
    <div v-if="hasTextSlot" class="button-text">
      <slot />
    </div>
  </button>
</template>

<style scoped lang="scss">
@import '../../styles/_media';
@import '../../styles/_fonts.scss';
.app-form-button {
  border: none;
  outline: none;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius-8);
  transition: var(--transition-very-short);
  color: var(--color-neutral-70);

  @include on-hover {
    cursor: pointer;
  }

  &:active {
    transform: matrix(0.95, 0, 0, 0.95, 0, 0);
  }

  &.enabled {
    background-color: var(--color-neutral-10);
    @include on-hover {
      background-color: var(--color-neutral-30);
    }
  }

  &.disabled {
    background-color: var(--color-neutral-30);
    color: var(--color-neutral-50);
    cursor: default;
    pointer-events: none;
  }

  &.wide {
    width: 100%;
  }

  // Text align
  &.left-icon {
    flex-direction: row-reverse;
    &.align-left {
      justify-content: flex-end;
    }

    &.align-right {
      justify-content: flex-start;
    }
  }

  &.right-icon {
    flex-direction: row;
  }

  &.align-left {
    justify-content: flex-start;
  }

  &.align-right {
    justify-content: flex-end;
  }

  // Sizes
  &.xxl {
    --size-xl: 56px;
    @include font-label-l-bold;
    height: var(--size-xl);
    min-width: var(--size-xl);
    padding: 0 var(--spacing-24);
    gap: var(--spacing-12);

    &.right-padding {
      padding-right: var(--spacing-32);
    }
    &.left-padding {
      padding-left: var(--spacing-32);
    }
  }

  &.xl {
    --size-xl: 52px;
    @include font-label-l-bold;
    height: var(--size-xl);
    min-width: var(--size-xl);
    padding: 0 var(--spacing-24);
    gap: var(--spacing-12);

    &.right-padding {
      padding-right: var(--spacing-32);
    }
    &.left-padding {
      padding-left: var(--spacing-32);
    }
  }

  &.l {
    --size-l: 44px;
    @include font-label-m-bold;
    height: var(--size-l);
    min-width: var(--size-l);
    padding: 0 var(--spacing-24);
    gap: var(--spacing-12);
    &.right-padding {
      padding-right: var(--spacing-32);
    }
    &.left-padding {
      padding-left: var(--spacing-32);
    }
  }

  &.m {
    --size-m: 36px;
    @include font-label-s-bold;
    height: var(--size-m);
    min-width: var(--size-m);
    padding: 0 var(--spacing-12);
    gap: var(--spacing-8);
    &.right-padding {
      padding-right: var(--spacing-16);
    }
    &.left-padding {
      padding-left: var(--spacing-16);
    }
  }

  .button-text {
    text-wrap: nowrap;
    transform: translateY(1px);
    overflow: hidden;
    text-overflow: ellipsis;
    // NOTE: Fix for Safari
    white-space: nowrap;
  }

  &.square {
    padding: 0;
  }
}
</style>
